import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";

import { HavenRealtimeProvider } from "../../Context/HavenRealtimeProvider.js";
import { UserProvider, useUser } from "../../Context/UserContext.js";
import { useSessionContextGate } from "../../Context/useSessionContextGate.js";
import { MainLayout } from "../../layout/MainLayout.js";
import { ErrorPage } from "../../pages/Error/ErrorPage.js";
import { createRoute } from "../../routing/createRoute.js";
import { rendersWithoutSessionContext } from "../../routing/sessionContextFreeRoutes.js";
import { SignOutButton } from "../SignOutButton/SignOutButton.js";

/**
 * Wraps every authenticated route. The session is fetched once here rather than
 * per page, so a page can assume `useUser().user` is populated and acting in a
 * chosen context by the time it renders.
 */
export const RootComponent = () => {
  return (
    <UserProvider>
      <HavenRealtimeProvider>
        <RootComponentContent />
      </HavenRealtimeProvider>
    </UserProvider>
  );
};

/**
 * Exported separately because `RootComponent` supplies the provider its content
 * reads from, which means a caller cannot substitute a session from outside it.
 * Stories and tests drive the states below by rendering this with a
 * `UserContext` of their own.
 */
export const RootComponentContent = () => {
  const { user, loading, error, refetchUser } = useUser();
  const gate = useSessionContextGate(user, refetchUser);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  if (loading || (user !== undefined && gate === "resolving")) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        role="status"
        aria-label={t("common.loading")}
      >
        <CircularProgress />
      </Box>
    );
  }

  // An unauthenticated response is handled by the axios interceptor, which
  // navigates to the sign-in page. Reaching here without a user means the request
  // itself failed or its body did not parse, which is worth showing.
  if (error || !user) {
    return <ErrorPage message={t("errors.userLoadFailed")} />;
  }

  if (gate === "failed") {
    return <ErrorPage message={t("errors.sessionContextFailed")} />;
  }

  // Belonging to nothing is an administrative problem, not an error the user can
  // retry their way out of, so it says what to do rather than offering a reload.
  //
  // It names the account and offers sign-out because this state renders no
  // navigation: without them the page is one a user cannot leave, and cannot
  // tell whether they are stuck as themselves or as someone else who was left
  // signed in. It links to their own account for the same reason — that page
  // needs no membership, so belonging to nothing must not put it out of reach.
  //
  // A path that renders without a membership is exempt rather than blocked —
  // see `rendersWithoutSessionContext`.
  if (gate === "no-memberships" && !rendersWithoutSessionContext(pathname)) {
    return (
      <Box
        sx={{
          maxWidth: 640,
          mx: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Alert severity="warning">{t("session.noMemberships")}</Alert>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("auth.signedInAs")} {user.email}
          </Typography>
          <Link
            component={RouterLink}
            to={createRoute.profile()}
            variant="body2"
          >
            {t("profile.openYours")}
          </Link>
          <SignOutButton />
        </Box>
      </Box>
    );
  }

  // Rendered without the shell: until a context is chosen there is no team name
  // to put in the header, and offering navigation would invite the user around
  // the choice rather than through it.
  if (gate === "choosing") {
    return <Outlet />;
  }

  return <MainLayout />;
};
